// Who gets offered the trial.
//
// US talent submit free and are NOT prompted for a trial. Everyone else is
// offered the 30-day trial (Step 4). That single rule drives three things, so
// it lives in one place rather than as a `=== "US"` sprinkled through the flow:
//
//   1. the reassurance line in the blue header ("free for US talent" vs "free
//      for your first 30 days"),
//   2. whether Step 4 shows the trial offering at all,
//   3. which plan/region the account is priced in — changing country changes
//      plans, so a home-country correction re-runs this.
//
// The country is the ACCOUNT's, not the IP's, once the account exists: an IP
// guess decides what we ask, never what someone is charged. The server is the
// authority at Step 4; this is what the client renders in the meantime.

import i18next from "i18next";

const NO_TRIAL_COUNTRIES = ["US"];

const upper = (value?: string | null) => (value || "").trim().toUpperCase();

// True when the visitor should be offered the trial. Unknown country → true:
// the trial is what every account gets by default, so it is the safe thing to
// promise when the lookup failed (and the alternative would advertise free
// submissions to someone who does not get them).
export const isTrialPrompted = (countryCode?: string | null): boolean => {
  const code = upper(countryCode);
  if (!code) return true;
  return NO_TRIAL_COUNTRIES.indexOf(code) < 0;
};

// True when submitting is free with no trial attached — today, US only.
export const submitsFree = (countryCode?: string | null): boolean =>
  !isTrialPrompted(countryCode);

// The reassurance sentence itself, so the two pages that show it cannot drift.
// The boost page used to hard-code the 30-day line, which meant a US visitor was
// promised a trial on the entry page and told submitting was free one screen
// later — the flow contradicting itself about money.
export const trialReassurance = (
  countryCode?: string | null,
  trialDays?: number | null,
  // A casting call anyone may submit to free (allowFreeSubmissions): true
  // wherever the visitor is, so it beats the country's promise.
  freeCastingCall?: boolean
): string =>
  freeCastingCall
    ? i18next.t("Submitting to this casting call is free")
    : submitsFree(countryCode)
    ? i18next.t("Submitting is free for US talent")
    : // Also the fallback when the lookup failed: every account gets the trial,
      // so it is the safe thing to promise when we cannot tell where the
      // visitor is.
      i18next.t("Submitting is free for your first {{count}} days.", {
        count: trialDays || 30,
      });
